import get from 'lodash/get';

import { BASE_INFO_OPTION_META_KEYS } from '../../../capabilities/base-info/fieldKeys';
import { EBM_RESOURCE_CONFIG_OPTION_META_KEYS } from '../../../capabilities/ebm-nodes/fieldKeys';
import { buildEbmCompositionPreviewRows } from '../../../capabilities/ebm-nodes/utils/formatResourceConfigurationDisplay';
import { NETWORK_OPTION_META_KEYS } from '../../../capabilities/network/fieldKeys';
import { serializeServiceListForApi } from '../../../capabilities/service/serviceApiField';
import type { DataDiskRecord } from '../../../capabilities/storage/types';
import { normalizeDataDiskList } from '../../../capabilities/storage/utils';
import type { PipelineContext } from '../../../core/types';
import type { DisplayDescriptor } from '../../../platform/display-descriptors';
import { resolveOptionDisplayValue } from '../../../platform/option-meta';
import type { ResourceType } from '../../../platform/pipeline-kernel/types';
import { resolvePricingRequirement, stripEmptyObjects } from '../../../utils';
import { buildResourceConfigFromPricing, pickPricingResourceMeta } from '../../../utils/pricingResourceMeta';
import { resolveEnabledModulesForSpecFilter } from '../../_shared/common/sku/resolveEnabledModulesForSpecFilter';
import { ebmCpu, ebmDataStorage, ebmElasticBareMetalNode, ebmGpu, ebmMemory, ebmNic, ebmNvme, ebmSystemStorage } from '../resourceConfigs';
import { filterEbmSpecByEnabledModules, isEbmModuleEnabled } from './enabledModules';
import type { EbmApiPayload, EbmSpec } from './schema';
import { parseEbmResourceConfiguration, serializeEbmSystemDiskVolume } from './spec';

function previewRow(id: string, group: string, label: string, value: unknown): DisplayDescriptor {
  const text = value === undefined || value === null || String(value).trim() === '' ? undefined : String(value);
  return { id, group, label, value: text as string };
}

/** 系统镜像：优先名称，其次地址 */
function resolveEbmSystemImageLabel(image?: { name?: string; url?: string }): string | undefined {
  const name = image?.name?.trim();
  if (name) return name;
  const url = image?.url?.trim();
  return url || undefined;
}

export function serializeEbmApiBody(spec: EbmSpec, ctx: PipelineContext): EbmApiPayload {
  const bundle = parseEbmResourceConfiguration(spec.resourceConfigurationRaw);
  const creator = ctx.overlay?.creator ?? '';
  const { volumes, system: systemFromDisk, image, source } = serializeEbmSystemDiskVolume(spec.systemDisk, creator);
  const reclaimPolicy = volumes[0]?.reclaimPolicy ?? 'Delete';

  const system = {
    ...systemFromDisk,
    ...(image ? { image } : {}),
    ...(source !== undefined ? { source } : {}),
  };

  const payload: EbmApiPayload = {
    alias: spec.alias ?? '',
    description: spec.description,
    runStrategy: spec.runStrategy || 'Always',
    workspace: spec.workspace ?? '',
    creator,
    system,
    resource: {
      ...(bundle?.cpu ? { cpu: bundle.cpu } : {}),
      ...(bundle?.memory ? { memory: bundle.memory } : {}),
      ...(bundle?.gpu ? { gpu: bundle.gpu } : {}),
      network: {
        vpc: spec.resource.network.vpc,
        subnet: spec.resource.network.subnet ?? 'default',
        securitygroup: spec.resource.network.securitygroup,
        dnsConfig: spec.resource.network.dnsConfig,
        service: serializeServiceListForApi(spec.resource.network.service),
        ...(Array.isArray(bundle?.nic) && bundle.nic.length ? { nic: bundle.nic } : {}),
      },
      storage: {
        ...(Array.isArray(bundle?.storage) && bundle.storage.length ? { device: bundle.storage } : {}),
        volume: [
          ...volumes,
          {
            type: 'CloudInitNoCloud',
            name: 'cloudinit',
            reclaimPolicy,
          },
        ],
      },
    },
  };

  if (spec.datacenter) {
    payload.datacenter = spec.datacenter;
  }

  return stripEmptyObjects(payload);
}

export function pickEbmPresetFields(spec: EbmSpec, ctx: PipelineContext): unknown {
  const enabledModules = resolveEnabledModulesForSpecFilter(ctx, {});
  const filtered = enabledModules !== undefined ? filterEbmSpecByEnabledModules(spec, enabledModules, ctx.sku?.skuType) : spec;

  return {
    alias: filtered.alias,
    description: filtered.description,
    runStrategy: filtered.runStrategy,
    workspace: filtered.workspace,
    systemDisk: filtered.systemDisk,
    resourceConfiguration: filtered.resourceConfigurationRaw,
    resource: {
      network: filtered.resource.network,
    },
    ...(filtered.optionMeta ? { optionMeta: filtered.optionMeta } : {}),
  };
}

export function buildEbmDisplayDescriptors(spec: EbmSpec, ctx: PipelineContext): DisplayDescriptor[] {
  /** 下单 enabledModules={} → undefined：不过滤，有配置的字段都会进预览 */
  const enabledModules = resolveEnabledModulesForSpecFilter(ctx, {});
  const skuType = ctx.sku?.skuType;
  const skipModuleFilter = enabledModules === undefined;
  const filtered = skipModuleFilter ? spec : filterEbmSpecByEnabledModules(spec, enabledModules, skuType);
  const optionMetaForm = spec.optionMeta ? { optionMeta: spec.optionMeta } : {};
  const bundle = parseEbmResourceConfiguration(filtered.resourceConfigurationRaw);
  const network = filtered.resource.network;
  const modOn = (key: string) => skipModuleFilter || isEbmModuleEnabled(key, enabledModules, skuType);

  const baseInfo: DisplayDescriptor[] = modOn('baseInfo')
    ? [
        previewRow('alias', 'baseInfo', '名称', filtered.alias),
        previewRow('description', 'baseInfo', '描述', filtered.description),
        previewRow(
          'workspace',
          'baseInfo',
          '租户空间',
          resolveOptionDisplayValue(optionMetaForm, 'workspace', filtered.workspace, BASE_INFO_OPTION_META_KEYS.workspace),
        ),
        previewRow('namespace', 'baseInfo', '命名空间', ctx.namespace),
      ]
    : [];

  const networkItems: DisplayDescriptor[] = modOn('network')
    ? [
        previewRow(
          'vpc',
          'network',
          'VPC',
          resolveOptionDisplayValue(optionMetaForm, 'resource.network.vpc', network.vpc, NETWORK_OPTION_META_KEYS.vpc),
        ),
        previewRow(
          'securitygroup',
          'network',
          '安全组',
          resolveOptionDisplayValue(
            optionMetaForm,
            'resource.network.securitygroup',
            network.securitygroup,
            NETWORK_OPTION_META_KEYS.securitygroup,
          ),
        ),
      ]
    : [];

  const ebmNodeMeta = filtered.optionMeta?.[EBM_RESOURCE_CONFIG_OPTION_META_KEYS.ebmNode] as
    | { vendor?: string; device?: string; name?: string; label?: string }
    | undefined;
  const ebmNodeLabel =
    ebmNodeMeta?.label?.trim() ||
    ebmNodeMeta?.name?.trim() ||
    [ebmNodeMeta?.vendor, ebmNodeMeta?.device].filter(Boolean).join(' / ') ||
    undefined;

  const compositionRows = buildEbmCompositionPreviewRows(bundle);
  const resourceItems: DisplayDescriptor[] = modOn('resource')
    ? [
        ...(ebmNodeLabel ? [previewRow('ebmNode', 'resource', '厂商设备', ebmNodeLabel)] : []),
        ...compositionRows.map((row) => previewRow(row.id, 'resource', row.label, row.value)),
      ]
    : [];

  const systemImage = filtered.systemDisk?.image;
  const systemSetting: DisplayDescriptor[] = modOn('systemSetting')
    ? [
        previewRow('runStrategy', 'systemSetting', '运行策略', filtered.runStrategy),
        previewRow('systemImage', 'systemSetting', '镜像', resolveEbmSystemImageLabel(systemImage)),
        previewRow('os', 'systemSetting', '操作系统', systemImage?.os?.trim()),
        previewRow('architecture', 'systemSetting', '架构', systemImage?.architecture?.trim()),
        previewRow(
          'systemDisk',
          'systemSetting',
          '系统盘大小',
          filtered.systemDisk?.diskSize != null ? `${filtered.systemDisk.diskSize} Gi` : undefined,
        ),
      ]
    : [];

  const dnsItems: DisplayDescriptor[] = modOn('dns')
    ? [
        previewRow('dnsNameservers', 'dns', 'DNS 服务器', network.dnsConfig?.nameservers?.join('、')),
        previewRow('dnsSearches', 'dns', 'DNS 搜索域', network.dnsConfig?.searches?.join('、')),
      ]
    : [];

  return [...baseInfo, ...networkItems, ...resourceItems, ...systemSetting, ...dnsItems];
}

function readHardwareResourceSize(record?: Record<string, unknown>): string | undefined {
  const size = get(record, 'resource.size');
  if (size == null || String(size).trim() === '') return undefined;
  /** 去掉容量单位后缀（Mi/Gi/Ti…），requirement 仅保留数值 */
  return String(size)
    .trim()
    .replace(/(Ei|Pi|Ti|Gi|Mi|Ki|[EPTGMK])$/i, '')
    .trim();
}

function pickHardwareResourceMeta(
  record: Record<string, unknown> | undefined,
  paymentTiming?: string,
  purpose?: PipelineContext['resourceQuantityPurpose'],
  skuType?: string,
): Pick<ResourceType, 'vendor' | 'device' | 'vendorID' | 'deviceID' | 'deviceType' | 'requirement' | 'unit'> | null {
  const requirement = resolvePricingRequirement(readHardwareResourceSize(record), {
    paymentTiming,
    skuType,
    vendorMeta: record,
    purpose,
  });
  if (!requirement) return null;
  return pickPricingResourceMeta(
    {
      vendor: record?.vendor != null ? String(record.vendor) : undefined,
      device: record?.device != null ? String(record.device) : undefined,
      vendorID: record?.vendorID != null ? String(record.vendorID) : undefined,
      deviceID: record?.deviceID != null ? String(record.deviceID) : undefined,
      deviceType: record?.deviceType != null ? String(record.deviceType) : undefined,
      quantityUnit: record?.quantityUnit != null ? String(record.quantityUnit) : undefined,
    },
    requirement,
  );
}

function pickMemoryResourceMeta(
  memory: Record<string, unknown> | undefined,
  paymentTiming?: string,
  purpose?: PipelineContext['resourceQuantityPurpose'],
  skuType?: string,
) {
  const requirement = resolvePricingRequirement(readHardwareResourceSize(memory), {
    paymentTiming,
    skuType,
    vendorMeta: memory,
    purpose,
  });
  if (!requirement) return null;
  const deviceType = memory?.deviceType != null ? String(memory.deviceType) : undefined;
  return pickPricingResourceMeta(
    {
      device: deviceType,
      deviceType,
      quantityUnit: memory?.quantityUnit != null ? String(memory.quantityUnit) : undefined,
    },
    requirement,
  );
}

function hasConfiguredHardwareResource(
  record?: Record<string, unknown>,
  paymentTiming?: string,
  purpose?: PipelineContext['resourceQuantityPurpose'],
  skuType?: string,
): boolean {
  return Boolean(pickHardwareResourceMeta(record, paymentTiming, purpose, skuType));
}

export function buildEbmResourceConfigs(spec: EbmSpec, ctx: PipelineContext): ResourceType[] {
  const configs: ResourceType[] = [];
  const paymentTiming = ctx.paymentTiming;
  const purpose = ctx.resourceQuantityPurpose;
  const enabledModules = resolveEnabledModulesForSpecFilter(ctx, {});
  const skuType = ctx.sku?.skuType;
  const skipModuleFilter = enabledModules === undefined;
  const filtered = skipModuleFilter ? spec : filterEbmSpecByEnabledModules(spec, enabledModules, skuType);
  const resourceOn = skipModuleFilter || isEbmModuleEnabled('resource', enabledModules, skuType);
  const systemOn = skipModuleFilter || isEbmModuleEnabled('systemSetting', enabledModules, skuType);

  if (resourceOn) {
    /** 计价模式：优先输出复合资源 ElasticBareMetalNode，避免与原子资源双计费 */
    const nodeMeta = filtered.optionMeta?.[EBM_RESOURCE_CONFIG_OPTION_META_KEYS.ebmNode] as Record<string, unknown> | undefined;
    if (nodeMeta?.billItemId || nodeMeta?.vendorID || nodeMeta?.deviceID) {
      // 复合节点选型无独立数量字段，选中即 1 台
      configs.push(
        buildResourceConfigFromPricing(
          ebmElasticBareMetalNode,
          {
            vendor: nodeMeta.vendor != null ? String(nodeMeta.vendor) : undefined,
            device: nodeMeta.device != null ? String(nodeMeta.device) : undefined,
            vendorID: nodeMeta.vendorID != null ? String(nodeMeta.vendorID) : undefined,
            deviceID: nodeMeta.deviceID != null ? String(nodeMeta.deviceID) : undefined,
            deviceType: nodeMeta.deviceType != null ? String(nodeMeta.deviceType) : undefined,
            quantityUnit: nodeMeta.quantityUnit != null ? String(nodeMeta.quantityUnit) : undefined,
          },
          '1',
        ),
      );
    } else {
      const cpuMeta = pickHardwareResourceMeta(filtered.resource.cpu, paymentTiming, purpose, skuType);
      if (cpuMeta) {
        configs.push({ ...ebmCpu, ...cpuMeta });
      }

      const memoryMeta = pickMemoryResourceMeta(filtered.resource.memory, paymentTiming, purpose, skuType);
      if (memoryMeta) {
        configs.push({ ...ebmMemory, ...memoryMeta });
      }

      const gpuMeta = pickHardwareResourceMeta(filtered.resource.gpu, paymentTiming, purpose, skuType);
      if (gpuMeta) {
        configs.push({ ...ebmGpu, ...gpuMeta });
      }

      let nicIndex = 0;
      filtered.resource.network.nic?.forEach((item) => {
        if (!hasConfiguredHardwareResource(item, paymentTiming, purpose, skuType)) return;
        const meta = pickHardwareResourceMeta(item, paymentTiming, purpose, skuType);
        if (!meta) return;
        configs.push({
          ...ebmNic,
          description: nicIndex > 0 ? `${ebmNic.description}${nicIndex + 1}` : ebmNic.description,
          ...meta,
        });
        nicIndex += 1;
      });

      let nvmeIndex = 0;
      filtered.resource.storage?.device?.forEach((item) => {
        if (!hasConfiguredHardwareResource(item, paymentTiming, purpose, skuType)) return;
        const meta = pickHardwareResourceMeta(item, paymentTiming, purpose, skuType);
        if (!meta) return;
        configs.push({
          ...ebmNvme,
          description: nvmeIndex > 0 ? `${ebmNvme.description}${nvmeIndex + 1}` : ebmNvme.description,
          ...meta,
        });
        nvmeIndex += 1;
      });
    }
  }

  if (systemOn && spec.systemDisk && spec.systemDisk.sourceKind !== 'existing') {
    const pricing = spec.systemDisk.pricing;
    const requirement = resolvePricingRequirement(spec.systemDisk.diskSize, { paymentTiming, skuType, vendorMeta: pricing, purpose });
    if (requirement != null) {
      configs.push(buildResourceConfigFromPricing(ebmSystemStorage, pricing, requirement));
    }
  }

  let dataDiskIndex = 0;
  normalizeDataDiskList((spec as EbmSpec & { dataDisks?: DataDiskRecord[] }).dataDisks).forEach((disk) => {
    if (disk.sourceKind === 'existing') return;
    const pricing = disk.pricing;
    const requirement = resolvePricingRequirement(disk.diskSize, { paymentTiming, skuType, vendorMeta: pricing, purpose });
    if (requirement == null) return;
    configs.push({
      ...buildResourceConfigFromPricing(ebmDataStorage, pricing, requirement),
      description: dataDiskIndex > 0 ? `${ebmDataStorage.description}${dataDiskIndex + 1}` : ebmDataStorage.description,
    });
    dataDiskIndex += 1;
  });

  return configs;
}
